import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CalendarDays,
  ChevronLeft,
  DollarSign,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { GoalForm } from '@/components/goals/GoalForm';
import { GoalSteps } from '@/components/goals/GoalSteps';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { GoalRow, GoalStatus } from '@/db/types';
import { addExpense } from '@/lib/expenses';
import { deleteGoal, getGoalById, updateGoal } from '@/lib/goals';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';

const STATUS_LABEL: Record<GoalStatus, string> = {
  someday: 'Someday',
  planning: 'Planning',
  'in-progress': 'In progress',
  done: 'Done',
};

/**
 * Project detail. VIEW-FIRST (Wave 60, user call): links from Today and
 * the projects list land on a readable detail page — title, status,
 * target, budget, the why, and the interactive step list — with the
 * full edit form one tap away behind the Edit button. Previously this
 * route rendered the raw edit form, which read as "editing the project
 * instead of viewing it."
 */
export default function ProjectDetailScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [row, setRow] = useState<GoalRow | null>(null);
  const [working, setWorking] = useState(false);
  const [editing, setEditing] = useState(false);

  const refresh = async () => {
    if (!id) return;
    setRow(await getGoalById(db, id));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, id]);

  if (!row) return <View style={styles.container} />;

  const confirmRemove = async () => {
    const ok = await confirm({
      title: 'Delete this?',
      message: 'It will disappear from your goals & projects list and budgets.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setWorking(true);
    try {
      await deleteGoal(db, row.id);
      goBackOrTo(router, '/projects');
    } finally {
      setWorking(false);
    }
  };

  if (editing) {
    return (
      <View style={styles.container}>
        <GoalForm
          mode="edit"
          initial={row}
          saving={working}
          onCancel={() => setEditing(false)}
          onSubmit={async (input) => {
            setWorking(true);
            try {
              const movedToDone = row.status !== 'done' && input.status === 'done';
              await updateGoal(db, row.id, input);
              if (movedToDone && (input.est_cost_cents ?? 0) > 0) {
                const ok = await confirm({
                  title: 'Log the cost as an expense?',
                  message: `You marked "${input.title}" done. Want to log $${((input.est_cost_cents ?? 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} as an expense so it shows up in your annual rollup?`,
                  confirmLabel: 'Log it',
                  cancelLabel: 'Skip',
                });
                if (ok) {
                  const prop = await getActiveProperty(db);
                  if (prop) {
                    await addExpense(db, {
                      propertyId: prop.id,
                      category: input.category ?? 'Big repair',
                      vendor: null,
                      amount_cents: input.est_cost_cents ?? 0,
                      paid_on: Date.now(),
                      description: `Goal: ${input.title}`,
                      recurring: null,
                      tax_deductible: 0,
                      notes: input.why ?? null,
                    });
                  }
                }
              }
              await refresh();
              setEditing(false);
            } finally {
              setWorking(false);
            }
          }}
          extraFooter={
            <Pressable
              onPress={confirmRemove}
              disabled={working}
              style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.85 }]}>
              <Trash2 size={16} color={Palette.clay} strokeWidth={1.8} />
              <Text style={styles.removeText}>Delete</Text>
            </Pressable>
          }
        />
      </View>
    );
  }

  const targetLabel = row.target_date
    ? new Date(row.target_date).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      })
    : null;
  const costLabel = row.est_cost_cents
    ? `$${(row.est_cost_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : null;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/projects')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Project</Text>
        <Pressable
          onPress={() => setEditing(true)}
          accessibilityLabel="Edit this project"
          style={({ pressed }) => [styles.editBtn, pressed && { opacity: 0.85 }]}>
          <Pencil size={13} color={Palette.forest} strokeWidth={2} />
          <Text style={styles.editBtnText}>Edit</Text>
        </Pressable>
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 60 }}
        showsVerticalScrollIndicator={false}>
        <View style={styles.hero}>
          <View style={[styles.statusPill, row.status === 'in-progress' && styles.statusPillActive]}>
            {row.status === 'in-progress' ? (
              <Sparkles size={12} color={Palette.honeyDeep} strokeWidth={2} />
            ) : null}
            <Text style={styles.statusPillText}>{STATUS_LABEL[row.status] ?? row.status}</Text>
          </View>
          <Text style={styles.title}>{row.title}</Text>
          {row.why ? <Text style={styles.why}>{row.why}</Text> : null}

          {targetLabel || costLabel ? (
            <View style={styles.metaRow}>
              {targetLabel ? (
                <View style={styles.metaChip}>
                  <CalendarDays size={13} color={Palette.forest} strokeWidth={1.9} />
                  <Text style={styles.metaChipText}>{targetLabel}</Text>
                </View>
              ) : null}
              {costLabel ? (
                <View style={styles.metaChip}>
                  <DollarSign size={13} color={Palette.forest} strokeWidth={1.9} />
                  <Text style={styles.metaChipText}>{costLabel} budgeted</Text>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>

        {/* Steps ARE the working surface — check things off right here. */}
        <GoalSteps db={db} goal={row} onChange={refresh} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  editBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 13, paddingVertical: 8,
    borderRadius: 999, borderWidth: 1.5, borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  editBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest,
    letterSpacing: 0.2,
  },
  hero: {
    marginTop: Spacing.two,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  statusPill: {
    alignSelf: 'flex-start',
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: Palette.sand,
    borderWidth: 1, borderColor: Palette.line,
  },
  statusPillActive: {
    backgroundColor: 'rgba(225,163,61,0.16)',
    borderColor: 'rgba(225,163,61,0.5)',
  },
  statusPillText: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkSoft,
    letterSpacing: 0.4, textTransform: 'uppercase',
  },
  title: {
    marginTop: 10,
    fontFamily: Fonts.serifBold, fontSize: 22, color: Palette.ink,
    letterSpacing: -0.3, lineHeight: 28,
  },
  why: {
    marginTop: 8,
    fontFamily: Fonts.sansMedium, fontSize: 13.5, color: Palette.inkSoft,
    lineHeight: 19,
  },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  metaChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 11, paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#EEF3EE',
    borderWidth: 1, borderColor: 'rgba(44,71,56,0.18)',
  },
  metaChipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  removeBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 14,
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
  },
  removeText: { color: Palette.clay, fontFamily: Fonts.sansBold, fontSize: 13.5 },
});
